import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogClose } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Card, CardContent } from './ui/card';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Eye,
  Calendar,
  User,
  Shield,
  FileText,
  Info,
  CheckSquare2,
  X,
  Download,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { handleAPIError } from '../utils/errorHandler';
import { actionPointAPI, reportAPI } from '../Services/apiService';
import { showToast } from '../utils/toast';
import ResponsiveTooltip from './ui/ResponsiveTooltip';
import { truncateText } from '../lib/utils';

const ActionPointsViewModal = ({ open, onClose, meeting }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [actionPoints, setActionPoints] = useState([]);
  const [actionTypePoints, setActionTypePoints] = useState([]);
  const [informationTypePoints, setInformationTypePoints] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);

  // Fetch action points when modal opens
  useEffect(() => {
    const fetchActionPoints = async () => {
      if (open && meeting?.id) {
        setIsLoading(true);
        try {
          const response = await actionPointAPI.getActionPointsForMeeting(meeting.id,{
            occurrence_date: meeting.scheduled_date
          });
          const points = response.data || [];
          setActionPoints(points);

          // Separate action and information points
          const actionPoints = points.filter(ap => ap.point_type === 'action');
          const informationPoints = points.filter(ap => ap.point_type === 'information');

          setActionTypePoints(actionPoints);
          setInformationTypePoints(informationPoints);

          console.log('Loaded action points:', actionPoints.length, 'action,', informationPoints.length, 'information');
        } catch (error) {
          handleAPIError(error, 'Failed to fetch action points', true);
          setActionPoints([]);
          setActionTypePoints([]);
          setInformationTypePoints([]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchActionPoints();
  }, [open, meeting?.id]);

  const handleDownloadExcel = async () => {
    if (!meeting?.id) return;
    try {
      setIsDownloading(true);
      const response = await reportAPI.exportMeetingMOM(meeting.id);

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const fileName = `MOM_${meeting.title.replace(/\s+/g, '_')}_${meeting.scheduled_date || 'Export'}.xlsx`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success('Excel downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      handleAPIError(error, 'Failed to download Excel', true);
    } finally {
      setIsDownloading(false);
    }
  };

  const formatStatus = (status) => {
    if (status === 'completed') return 'Completed';
    if (status === 'approved') return 'Completed'; // Backward compatibility
    if (!status) return '';
    return status
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-0 hover:!bg-green-100';
      case 'approved':
        return 'bg-green-100 text-green-800 border-0 hover:!bg-green-100';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-0 hover:!bg-yellow-100';
      case 'submitted_for_review':
        return 'bg-purple-100 text-purple-800 border-0 hover:!bg-purple-100';
      case 'rejected_in_review':
        return 'bg-red-100 text-red-800 border-0 hover:!bg-red-100';
      default:
        return 'bg-gray-100 text-gray-800 border-0 hover:!bg-gray-100';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-6xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="sticky top-0 z-10 bg-white border-b px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">

              <DialogTitle className="flex items-center gap-2">

                Action Points & Information
              </DialogTitle>
              <DialogDescription className="">
                {meeting?.title && (
                  <ResponsiveTooltip
                    content={<p className="text-sm whitespace-pre-wrap">{meeting.title}</p>}
                    className="max-w-md p-3"
                  >
                    <p className="text-sm text-start font-medium cursor-pointer">
                      Meeting: {truncateText(meeting.title, 30)} |
                      {` ${meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd, yyyy') : ''}`}
                    </p>
                  </ResponsiveTooltip>
                )}
              </DialogDescription>
            </div>
            <DialogClose className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="text-center py-8 text-sm text-gray-500">
              Loading action points...
            </div>
          ) : actionPoints.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No action points or information available for this meeting</p>
            </div>
          ) : (
            <Tabs defaultValue="action" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="action" className="flex items-center gap-2">
                  <CheckSquare2 className="h-4 w-4" />
                  Action ({actionTypePoints.length})
                </TabsTrigger>
                <TabsTrigger value="information" className="flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Information ({informationTypePoints.length})
                </TabsTrigger>
              </TabsList>

              {/* Action Tab */}
              <TabsContent value="action" className="mt-4">
                {actionTypePoints.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckSquare2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No action points available</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden md:block rounded-lg border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="w-12 text-center">Sr.</TableHead>
                            <TableHead className="min-w-[250px]">Description / Task</TableHead>
                            <TableHead className="min-w-[140px]">Assignee</TableHead>
                            <TableHead className="min-w-[140px]">Approver</TableHead>
                            <TableHead className="w-32">Due Date</TableHead>
                            <TableHead className="w-28 text-center">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {actionTypePoints.map((ap, index) => (
                            <TableRow key={ap.id}>
                              <TableCell className="text-center font-medium text-gray-600">
                                {index + 1}
                              </TableCell>
                              <TableCell>
                                {ap.description && ap.description.length > 40 ? (
                                  <ResponsiveTooltip
                                    content={<p className="text-sm whitespace-pre-wrap">{ap.description}</p>}
                                    className="max-w-md p-3"
                                  >
                                    <p className="font-medium text-gray-900 cursor-pointer">
                                      {truncateText(ap.description, 40)}
                                    </p>
                                  </ResponsiveTooltip>
                                ) : (
                                  <p className="font-medium text-gray-900">{ap.description}</p>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4 text-green-600" />
                                  <span className="text-sm">
                                    {ap.assignees && ap.assignees.length > 0
                                      ? ap.assignees.map(a => a.full_name).join(', ')
                                      : ap.assignee?.full_name || 'N/A'}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Shield className="h-4 w-4 text-red-600" />
                                  <span className="text-sm">{ap.approver?.full_name || 'N/A'}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1 text-sm">
                                  <Calendar className="h-3 w-3 text-gray-400" />
                                  <span>{ap.due_date ? format(new Date(ap.due_date), 'MMM dd, yyyy') : 'N/A'}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge className={`${getStatusBadge(ap.status)} text-xs`}>
                                  {formatStatus(ap.status)}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3">
                      {actionTypePoints.map((ap, index) => (
                        <Card key={ap.id} className="border-2">
                          <CardContent className="p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-700">#{index + 1}</span>
                              <Badge className={`${getStatusBadge(ap.status)} text-xs`}>
                                {formatStatus(ap.status)}
                              </Badge>
                            </div>

                            <div className="space-y-2">
                              <div className='flex flex-col'>
                                <label className="text-xs font-medium text-gray-500">Description</label>
                                {ap.description && ap.description.length > 30 ? (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <span className="text-sm font-medium text-gray-900 text-left hover:underline cursor-pointer inline-block">
                                        {truncateText(ap.description, 30)}
                                      </span>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-72 p-3" align="start">
                                      <div className="text-sm font-medium text-gray-900 whitespace-pre-wrap">
                                        {ap.description}
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                ) : (
                                  <p className="text-sm font-medium text-gray-900">{ap.description || 'N/A'}</p>
                                )}
                              </div>

                              <div className="flex flex-wrap justify-between gap-2">
                                <div>
                                  <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                                    <User className="h-3 w-3" />
                                    Assignee
                                  </label>
                                  <p className="text-sm">
                                    {ap.assignees && ap.assignees.length > 0
                                      ? ap.assignees.map(a => a.full_name).join(', ')
                                      : ap.assignee?.full_name || 'N/A'}
                                  </p>
                                </div>

                                <div>
                                  <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                                    <Shield className="h-3 w-3" />
                                    Approver
                                  </label>
                                  <p className="text-sm">{ap.approver?.full_name || 'N/A'}</p>
                                </div>

                                <div>
                                  <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    Due Date
                                  </label>
                                  <p className="text-sm">{ap.due_date ? format(new Date(ap.due_date), 'MMM dd, yyyy') : 'N/A'}</p>
                                </div>
                              </div>


                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Information Tab */}
              <TabsContent value="information" className="mt-4">
                {informationTypePoints.length === 0 ? (
                  <div className="text-center py-8">
                    <Info className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No information points available</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden md:block rounded-lg border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="w-12 text-center">Sr.</TableHead>
                            <TableHead className="min-w-[400px]">Description</TableHead>
                            <TableHead className="w-40">Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {informationTypePoints.map((ap, index) => (
                            <TableRow key={ap.id}>
                              <TableCell className="text-center font-medium text-gray-600">
                                {index + 1}
                              </TableCell>
                              <TableCell>
                                {ap.description && ap.description.length > 60 ? (
                                  <ResponsiveTooltip
                                    content={<p className="text-sm whitespace-pre-wrap">{ap.description}</p>}
                                    className="max-w-md p-3"
                                  >
                                    <p className="text-sm text-gray-900 cursor-pointer">
                                      {truncateText(ap.description, 60)}
                                    </p>
                                  </ResponsiveTooltip>
                                ) : (
                                  <p className="text-sm text-gray-900">{ap.description}</p>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1 text-sm">
                                  <Calendar className="h-3 w-3 text-gray-400" />
                                  <span>{ap.created_at ? format(new Date(ap.created_at), 'MMM dd, yyyy') : 'N/A'}</span>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3">
                      {informationTypePoints.map((ap, index) => (
                        <Card key={ap.id} className="border-2">
                          <CardContent className="p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-700">#{index + 1}</span>
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <Calendar className="h-3 w-3" />
                                {ap.created_at ? format(new Date(ap.created_at), 'MMM dd, yyyy') : 'N/A'}
                              </div>
                            </div>

                            <div className='flex flex-col'>
                              <label className="text-xs font-medium text-gray-500">Description</label>
                              {ap.description && ap.description.length > 20 ? (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <button className="text-sm text-gray-900 mt-1 text-left hover:underline cursor-pointer">
                                      {truncateText(ap.description, 20)}
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-72 p-3" align="start">
                                    <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                      {ap.description}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              ) : (
                                <p className="text-sm text-gray-900 mt-1">{ap.description || 'N/A'}</p>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-between items-center">
          <Button
            type="button"
            variant="outline"
            onClick={handleDownloadExcel}
            disabled={isDownloading || actionPoints.length === 0}
            className="flex items-center gap-2 border-green-600 text-green-700 hover:bg-green-50"
          >
            {isDownloading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Download Excel
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActionPointsViewModal;

